/**
 * ActivityPub URL utilities
 *
 * Handles parsing and encoding of AP post URIs for routing
 */

import type { APPostUriParts } from '@/types/apThread'
// SDK provides a more comprehensive isSafeHostname that additionally blocks
// 127.x.x.x (all loopback, not just 127.0.0.1), 100.64.0.0/10 (carrier-grade NAT),
// .local/.localhost mDNS domains, ip6-localhost, and other reserved ranges.
import { isSafeHostname } from '@johnwhiteside/social-web-sdk'
export { isSafeHostname }

/**
 * Base64url encode a string (URL-safe base64)
 * Uses standard btoa but replaces URL-unsafe characters
 */
export function encodePostId(url: string): string {
    const base64 = btoa(url)
    // Make URL-safe: + -> -, / -> _, remove padding =
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Base64url decode a string back to original
 * Returns empty string if decoding fails
 */
export function decodePostId(postId: string): string {
    try {
        // Restore standard base64: - -> +, _ -> /
        let base64 = postId.replace(/-/g, '+').replace(/_/g, '/')
        // Add padding if needed
        const padding = base64.length % 4
        if (padding) {
            base64 += '='.repeat(4 - padding)
        }
        return atob(base64)
    } catch {
        // Invalid base64 input
        return ''
    }
}

/**
 * Parse an ActivityPub post URI into its components
 *
 * Supports common AP instance URIs:
 * - https://mastodon.social/users/Gargron/statuses/116053480625514636 (Mastodon)
 * - https://mastodon.social/@Gargron/116053480625514636 (Mastodon short form)
 * - https://pixelfed.social/p/alice/116053480625514636 (Pixelfed native)
 *
 * Also supports Pixelfed URIs:
 * - https://pixelfed.social/p/alice/123456789012345678
 *
 * @returns Parsed components or null if URI format is not recognized
 */
export function parseAPPostUri(uri: string): APPostUriParts | null {
    try {
        const url = new URL(uri)

        // Only accept http/https protocols
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return null
        }

        // Block private IPs and localhost to prevent SSRF
        if (!isSafeHostname(url.hostname)) {
            return null
        }

        const instanceHost = url.hostname
        const pathParts = url.pathname.split('/').filter(Boolean)

        // Try /users/{username}/statuses/{id} format (Mastodon)
        const usersIndex = pathParts.indexOf('users')
        if (usersIndex !== -1 && pathParts[usersIndex + 2] === 'statuses') {
            const username = pathParts[usersIndex + 1]
            const statusId = pathParts[usersIndex + 3]
            if (username && statusId) {
                return {
                    handle: `${username}@${instanceHost}`,
                    postId: statusId, // Use numeric ID directly
                    instanceHost,
                    username,
                }
            }
        }

        // Try /@{username}/{id} format (Mastodon short form)
        if (pathParts.length >= 2 && pathParts[0].startsWith('@')) {
            const username = pathParts[0].slice(1) // Remove @
            const statusId = pathParts[1]
            if (username && statusId && /^\d+$/.test(statusId)) {
                return {
                    handle: `${username}@${instanceHost}`,
                    postId: statusId, // Use numeric ID directly
                    instanceHost,
                    username,
                }
            }
        }

        // Try /p/{username}/{id} format (Pixelfed native ActivityPub URI)
        if (pathParts.length >= 3 && pathParts[0] === 'p') {
            const username = pathParts[1]
            const statusId = pathParts[2]
            if (username && statusId && /^\d+$/.test(statusId)) {
                return {
                    handle: `${username}@${instanceHost}`,
                    postId: statusId,
                    instanceHost,
                    username,
                }
            }
        }

        // URI didn't match any known format
        return null
    } catch {
        // Invalid URL format
        return null
    }
}

/**
 * Build the webapp route URL for an AP post
 *
 * @param handle - AP handle (e.g., "Gargron@mastodon.social")
 * @param postId - Numeric post ID (e.g., "116053310451860840")
 * @returns Route path (e.g., "/profile/Gargron@mastodon.social/post/116053310451860840")
 */
export function buildAPPostRoute(handle: string, postId: string): string {
    return `/profile/${handle}/post/${postId}`
}

/**
 * Build the webapp route URL from a full AP post URI
 *
 * @param uri - Full AP post URI (e.g., "https://mastodon.social/users/Gargron/statuses/123")
 * @returns Route path or null if URI cannot be parsed
 */
export function buildAPPostRouteFromUri(uri: string): string | null {
    const parsed = parseAPPostUri(uri)
    if (!parsed) return null
    return buildAPPostRoute(parsed.handle, parsed.postId)
}

/**
 * Check if a string looks like an ActivityPub post URI
 */
export function isAPPostUri(uri: string): boolean {
    if (!uri.startsWith('http://') && !uri.startsWith('https://')) {
        return false
    }
    return parseAPPostUri(uri) !== null
}

/**
 * Check if a URI is a Bridgy Fed bridged AT Protocol post
 * These have format: https://bsky.brid.gy/convert/ap/at://did:plc:.../app.bsky.feed.post/rkey
 *
 * @returns The extracted AT Protocol URI components or null
 */
export function parseBridgyFedUri(uri: string): { did: string; rkey: string } | null {
    try {
        const url = new URL(uri)

        // Only accept http/https protocols
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return null
        }

        // Block private IPs and localhost to prevent SSRF
        if (!isSafeHostname(url.hostname)) {
            return null
        }

        // Check if it's a Bridgy Fed convert URL
        if (!url.hostname.endsWith('brid.gy')) {
            return null
        }

        // Look for /convert/ap/at:// pattern
        const convertMatch = url.pathname.match(
            /\/convert\/ap\/at:\/\/([^/]+)\/app\.bsky\.feed\.post\/([^/]+)/
        )
        if (convertMatch) {
            return {
                did: convertMatch[1],
                rkey: convertMatch[2],
            }
        }

        return null
    } catch {
        return null
    }
}

/**
 * Build route for a post, handling both native AP and Bridgy Fed posts
 *
 * @param uri - Full post URI
 * @param authorHandle - Optional author handle
 * @param mastodonStatusId - Optional Mastodon status ID; when present for a Bridgy Fed post,
 *   routes to the AP thread view instead of the AT Protocol post view
 * @returns Route path or null if cannot be parsed
 */

/** Valid AP username characters (no spaces, @, or other special chars) */
const AP_SIMPLE_USERNAME_RE = /^[a-zA-Z0-9._-]+$/

export function buildPostRouteFromUri(
    uri: string,
    _authorHandle?: string,
    mastodonStatusId?: string
): string | null {
    // Check if it's a Bridgy Fed bridged AT Protocol post
    const bridgyParsed = parseBridgyFedUri(uri)
    if (bridgyParsed) {
        // When called from the AP timeline with a mastodonStatusId and a valid author handle,
        // route to the AP thread view so the post is viewed in its AP context.
        // The mastodonStatusId is the bsky.brid.gy Mastodon status ID for this post.
        if (mastodonStatusId && _authorHandle && AP_SIMPLE_USERNAME_RE.test(_authorHandle)) {
            try {
                const instance = new URL(uri).hostname // e.g. 'bsky.brid.gy'
                return `/profile/${_authorHandle}@${instance}/post/${mastodonStatusId}`
            } catch {
                // Fall through to AT Protocol route
            }
        }
        // Route to native Bluesky post view using the extracted DID and rkey
        return `/profile/${bridgyParsed.did}/post/${bridgyParsed.rkey}`
    }

    // Otherwise try native AP format
    const apParsed = parseAPPostUri(uri)
    if (apParsed) {
        // For Pixelfed URIs using /p/{username}/{id} format, encode the full URI.
        // buildFullPostUri reconstructs using Mastodon format (/users/.../statuses/...),
        // which differs from Pixelfed's ActivityPub ID format (/p/.../...).
        // Encoding preserves the original URI so the GraphQL fallback in fetchAPThread
        // uses the correct Pixelfed URI instead of the reconstructed Mastodon-style one.
        try {
            const url = new URL(uri)
            if (url.pathname.split('/').filter(Boolean)[0] === 'p') {
                const encodedUri = encodePostId(uri)
                return `/profile/${apParsed.handle}/post/${encodedUri}`
            }
        } catch {
            // Fall through to standard route
        }
        return buildAPPostRoute(apParsed.handle, apParsed.postId)
    }

    return null
}

/**
 * Build full AP post URI from handle and postId
 * Used for API calls that need the full URI
 *
 * @param handle - AP handle (e.g., "Gargron@mastodon.social")
 * @param postId - Numeric post ID or base64url-encoded URI (for Bridgy Fed posts)
 * @returns Full URI (e.g., "https://mastodon.social/users/Gargron/statuses/116053310451860840")
 */
export function buildFullPostUri(handle: string, postId: string): string {
    // Check if postId might be a base64url-encoded URI (Bridgy Fed posts)
    // Try to decode if it contains non-digit characters
    if (/[a-zA-Z_-]/.test(postId)) {
        const decoded = decodePostId(postId)
        if (decoded.startsWith('http')) {
            return decoded
        }
    }

    // Standard Mastodon format
    const [username, domain] = handle.split('@')
    return `https://${domain}/users/${username}/statuses/${postId}`
}

/**
 * Reconstruct full post URI from handle and postId (for API calls)
 * @deprecated Use buildFullPostUri instead
 */
export function getFullPostUri(postId: string): string {
    return decodePostId(postId)
}
// ci trigger

/**
 * Handle as shown on an ActivityPub post card. Prefixes `@` unless the handle
 * is URL-shaped (http/https) or already starts with `@`.
 */
export function formatAPDisplayHandle(handle: string): string {
    if (handle.startsWith('@') || /^https?:\/\//i.test(handle)) return handle
    return `@${handle}`
}
